/**
 * Fee resolution — docs/05 "Fees".
 *
 * Pure. No I/O, no ambient time, no random.
 *
 * Two shapes exist in merchant config:
 *   - `flat`    — an integer amount of halalas, charged once per returned line.
 *   - `percent` — whole percentage points of `unitPrice x qtyExpected` for that line.
 *
 * "No fee" has exactly ONE representation inside the engine: `NO_FEE`
 * (`{ type: 'flat', amount: 0 }`). Merchant config expresses it two ways — an absent key
 * in `policy.fees`, or a key whose `amount` is `0` — and both are normalised on read so
 * no downstream code has to ask which one it got.
 *
 * The fee is resolved ONCE, at creation, and stored on `return_order.fee_applied`.
 * Nothing recomputes it later (docs/05, ADR-006).
 *
 * These functions are also the ONLY sanctioned way to turn `PublicPolicy.fees` into the
 * disclosure the portal must show before submit and again at review (policy check 9). The
 * shopper-facing figure and the stored `fee_applied` have to agree to the halala, and the
 * only way to guarantee that is for both to run this arithmetic rather than two
 * reimplementations of it: `feeForReason()` + `computeLineFee()` per line, or
 * `resolveFeeApplied()` for the total. The client's figure remains a disclosure and never a
 * control — the server re-resolves from the snapshot at creation.
 */
import type {
  FeeConfig,
  LocalizedText,
  MoneyHalalas,
} from "@sirdab/returns-shared";
import { message } from "@sirdab/returns-shared";

/** The single canonical representation of "this reason carries no fee". */
export const NO_FEE: FeeConfig = Object.freeze({ type: "flat", amount: 0 });

/** A line as the fee calculation sees it. Money is integer halalas throughout. */
export interface FeeableLine {
  /**
   * Kept as `string` rather than `ReturnReasonCode`: `policy.fees` is a
   * `Record<string, FeeConfig>` and merchants configure reason vocabularies beyond the
   * canonical five (the `qut-foods` fixture adds `spoiled`). Lookup is a string lookup.
   */
  reasonCode: string;
  /** Integer halalas. */
  unitPrice: MoneyHalalas;
  /** `qty_expected` — what the shopper declared, never `qty_received`. */
  qty: number;
}

/** Absent key and a zero amount collapse to the same value. */
export function normaliseFee(fee: FeeConfig | null | undefined): FeeConfig {
  if (fee === null || fee === undefined) return NO_FEE;
  if (fee.amount === 0) return NO_FEE;
  return fee;
}

/** True when this config actually charges the shopper something. */
export function hasFee(fee: FeeConfig | null | undefined): boolean {
  return normaliseFee(fee).amount > 0;
}

/**
 * Normalise a whole `policy.fees` table on read. Every configured key survives (so the
 * merchant's intent is still visible) but every "no fee" entry becomes `NO_FEE`.
 */
export function normaliseFeeTable(
  fees: Record<string, FeeConfig> | null | undefined,
): Record<string, FeeConfig> {
  const out: Record<string, FeeConfig> = {};
  for (const [code, fee] of Object.entries(fees ?? {})) {
    out[code] = normaliseFee(fee);
  }
  return out;
}

/** The fee configured for a reason. An unconfigured reason is `NO_FEE`, never `undefined`. */
export function feeForReason(
  fees: Record<string, FeeConfig> | null | undefined,
  reasonCode: string,
): FeeConfig {
  return normaliseFee(fees?.[reasonCode]);
}

/**
 * The fee a single line contributes, in integer halalas.
 * `flat` is charged once for the line regardless of quantity; `percent` is of the line
 * value (`unitPrice x qty`) and is rounded to the nearest halala.
 */
export function computeLineFee(
  fee: FeeConfig | null | undefined,
  line: FeeableLine,
): MoneyHalalas {
  const resolved = normaliseFee(fee);
  if (resolved.amount === 0) return 0;
  if (resolved.type === "flat") return resolved.amount;
  return Math.round((line.unitPrice * line.qty * resolved.amount) / 100);
}

/**
 * The single `fee_applied` figure frozen onto the return at creation: the sum of every
 * requested line's fee under the policy that applied.
 */
export function resolveFeeApplied(input: {
  fees: Record<string, FeeConfig> | null | undefined;
  lines: readonly FeeableLine[];
}): MoneyHalalas {
  const table = normaliseFeeTable(input.fees);
  return input.lines.reduce(
    (total, line) =>
      total + computeLineFee(feeForReason(table, line.reasonCode), line),
    0,
  );
}

/** Bilingual copy for the fee, from the catalogue — never an inline string. */
export function describeFee(fee: FeeConfig | null | undefined): LocalizedText {
  const resolved = normaliseFee(fee);
  if (resolved.amount === 0) return message("fee.none");
  return resolved.type === "flat"
    ? message("fee.flat")
    : message("fee.percent");
}
